import { useCallback, useEffect, useMemo, useState } from "react";
import type { CoverageAreaInput, CoverageLookupHit, CoveragePartyInput } from "@/lib/coverage/coverageLookup";
import type { CoverageNote, CoverageSuggestion } from "@/lib/coverage/coverageNotes";
import { coverageProblems, type CoverageCampaign, type ProblemSettings } from "@/lib/coverage/coverageProblems";
import type { CoverageQuote, SaleDot, SaleIssue } from "@/lib/coverage/coverageSales";
import {
  fetchCoverageBoard,
  lookupCoverageAddress,
  milesToKm,
  saveCoverageAreas,
  saveCoverageNote,
  saveCoverageParty,
  suggestCoverageProvider,
  type CoverageLocationPin,
} from "@/lib/coverage/coverageClient";
import { draftFromParty, emptyPartyDraft, type PartyDraft } from "@/components/coverage/CoveragePartyEditor";

export function useCoverageBoard() {
  const [parties, setParties] = useState<CoveragePartyInput[]>([]);
  const [areas, setAreas] = useState<CoverageAreaInput[]>([]);
  const [notes, setNotes] = useState<CoverageNote[]>([]);
  const [campaigns, setCampaigns] = useState<CoverageCampaign[]>([]);
  const [settings, setSettings] = useState<ProblemSettings>({ overlapCount: 2, noteReviewDays: 30, minSpendUsd: 0, quietDays: 30 });
  const [quotes, setQuotes] = useState<CoverageQuote[]>([]);
  const [sales, setSales] = useState<SaleDot[]>([]);
  const [salesLoaded, setSalesLoaded] = useState(false);
  const [issues, setIssues] = useState<SaleIssue[]>([]);
  const [showCampaigns, setShowCampaigns] = useState(true);
  const [campaignDrawing, setCampaignDrawing] = useState(false);
  const [campaignPolygon, setCampaignPolygon] = useState<unknown | null>(null);
  const [locations, setLocations] = useState<CoverageLocationPin[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraft] = useState<PartyDraft>(emptyPartyDraft);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [address, setAddress] = useState("");
  const [program, setProgram] = useState("tube_steel");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [hits, setHits] = useState<CoverageLookupHit[]>([]);
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [precision, setPrecision] = useState<"rooftop" | "city" | null>(null);
  const [sizeLabel, setSizeLabel] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<CoverageSuggestion | null>(null);
  const [chosenPartyId, setChosenPartyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const res = await fetchCoverageBoard();
    if (!res.ok) {
      setLoadError(res.error);
      return;
    }
    setLoadError(null);
    setParties(res.data.parties);
    setAreas(res.data.areas);
    setNotes(res.data.notes ?? []);
    setCampaigns(res.data.campaigns ?? []);
    setSettings(res.data.settings ?? { overlapCount: 2, noteReviewDays: 30, minSpendUsd: 0, quietDays: 30 });
    setQuotes(res.data.quotes ?? []);
    setSales(Array.isArray(res.data.sales) ? res.data.sales : []);
    setSalesLoaded(Array.isArray(res.data.sales));
    setIssues(res.data.issues ?? []);
    setLocations(res.data.locations);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  function selectParty(id: string | null) {
    setSaveError(null);
    setDrawing(false);
    if (!id) {
      setDraft(emptyPartyDraft());
      return;
    }
    const party = parties.find((row) => row.id === id);
    if (!party) return;
    setDraft(draftFromParty(party, areas));
  }

  function acceptPolygon(geojson: unknown) {
    if (campaignDrawing) {
      setCampaignDrawing(false);
      setCampaignPolygon(geojson);
      return;
    }
    setDrawing(false);
    setDraft((current) => ({ ...current, polygonText: JSON.stringify(geojson) }));
  }

  async function save() {
    setSaving(true);
    setSaveError(null);
    const radii = parseRadii(draft.preferredMiles, draft.fullMiles);
    if (!radii.ok) {
      setSaving(false);
      setSaveError(radii.error);
      return;
    }
    let polygon: unknown = null;
    if (draft.polygonText.trim()) {
      try {
        polygon = JSON.parse(draft.polygonText) as unknown;
      } catch {
        setSaving(false);
        setSaveError("Polygon GeoJSON is not valid JSON.");
        return;
      }
    }
    const partyRes = await saveCoverageParty({
      id: draft.id,
      name: draft.name,
      program: draft.program,
      color: draft.color,
      pricing_url: draft.pricingUrl,
      address: draft.address,
      active: true,
    });
    if (!partyRes.ok) {
      setSaving(false);
      setSaveError(partyRes.error);
      return;
    }
    const partyId = partyRes.data.party.id;
    setDraft((current) => ({ ...current, id: partyId }));
    const areaRes = await saveCoverageAreas({
      party_id: partyId,
      preferred_radius_km: radii.preferred,
      full_radius_km: radii.full,
      polygon,
    });
    setSaving(false);
    if (!areaRes.ok) {
      setSaveError(areaRes.error);
      return;
    }
    setDraft((current) => ({ ...current, id: partyId, address: "" }));
    await reload();
  }

  async function lookup() {
    setLookupBusy(true);
    setLookupError(null);
    const res = await lookupCoverageAddress({ address, program });
    setLookupBusy(false);
    if (!res.ok) {
      setHits([]);
      setPoint(null);
      setPrecision(null);
      setLookupError(res.error);
      return;
    }
    setHits(res.data.hits);
    setPoint({ lat: res.data.point.lat, lng: res.data.point.lng });
    setPrecision(res.data.point.precision);
    setSuggestion(null);
    setChosenPartyId(null);
    setSuggestError(null);
  }

  async function suggest() {
    setSuggestBusy(true);
    setSuggestError(null);
    const res = await suggestCoverageProvider({ address, program, size_label: sizeLabel });
    setSuggestBusy(false);
    if (!res.ok) {
      setSuggestError(res.error);
      return;
    }
    setHits(res.data.hits);
    setPoint({ lat: res.data.point.lat, lng: res.data.point.lng });
    setPrecision(res.data.point.precision);
    setSuggestion(res.data.suggestion);
    setChosenPartyId(res.data.suggestion?.partyId ?? null);
  }

  async function changeNote(body: Record<string, unknown>) {
    setNoteBusy(true);
    setNoteError(null);
    const res = await saveCoverageNote(body);
    setNoteBusy(false);
    if (!res.ok) {
      setNoteError(res.error);
      return;
    }
    await reload();
  }

  const draftPolygon = campaignDrawing || campaignPolygon ? campaignPolygon : parseDraftPolygon(draft.polygonText);
  const problems = useMemo(
    () =>
      coverageProblems({
        parties,
        areas,
        notes,
        campaigns,
        sales: salesLoaded ? sales : undefined,
        settings,
        nowMs: Date.now(),
      }),
    [parties, areas, notes, campaigns, sales, salesLoaded, settings],
  );

  return {
    parties,
    areas,
    notes,
    campaigns,
    showCampaigns,
    setShowCampaigns,
    campaignDrawing,
    setCampaignDrawing,
    campaignPolygon,
    clearCampaignPolygon: () => setCampaignPolygon(null),
    problems,
    quotes,
    sales,
    issues,
    locations,
    reload,
    loadError,
    draft,
    setDraft,
    selectParty,
    save,
    saving,
    saveError,
    drawing,
    setDrawing,
    acceptPolygon,
    address,
    setAddress,
    program,
    setProgram,
    lookup,
    lookupBusy,
    lookupError,
    hits,
    point,
    precision,
    sizeLabel,
    setSizeLabel,
    suggestion,
    suggestBusy,
    suggestError,
    suggest,
    chosenPartyId,
    chooseParty: setChosenPartyId,
    noteBusy,
    noteError,
    saveNote: (input: { partyId: string; body: string; program: string; sizeLabel: string; reviewAfter: string }) =>
      changeNote({
        party_id: input.partyId,
        body: input.body,
        program: input.program,
        size_label: input.sizeLabel,
        review_after: input.reviewAfter,
      }),
    invalidateNote: (id: string) => changeNote({ op: "invalidate", id }),
    reconfirmNote: (id: string) => changeNote({ op: "reconfirm", id }),
    draftPolygon,
  };
}

function parseRadii(preferredMiles: string, fullMiles: string): { ok: true; preferred: number | null; full: number | null } | { ok: false; error: string } {
  const preferred = milesOrNull(preferredMiles);
  const full = milesOrNull(fullMiles);
  if (preferred === "bad" || full === "bad") return { ok: false, error: "Radius must be a number of miles." };
  if (preferred != null && full != null && preferred > full) {
    return { ok: false, error: "Preferred radius is larger than the full territory." };
  }
  return { ok: true, preferred, full };
}

function milesOrNull(text: string): number | null | "bad" {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const miles = Number(trimmed);
  if (!Number.isFinite(miles) || miles <= 0) return "bad";
  return milesToKm(miles);
}

function parseDraftPolygon(text: string): unknown | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return null;
  }
}
